/**
 * Build Catch Board owner Select options from Unassigned + assignees.
 * Keeps the current owner even if they are missing from the admin directory.
 *
 * @param {Array<{ accountId?: string, name?: string, email?: string }>} [assignees]
 * @param {{
 *   accountId?: string|null,
 *   accountLabel?: string,
 *   currentOwnerAccountId?: string|null,
 *   currentOwnerLabel?: string,
 * }} [options]
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildCatchBoardOwnerOptions(
  assignees = [],
  {
    accountId = null,
    accountLabel = '',
    currentOwnerAccountId = null,
    currentOwnerLabel = '',
  } = {},
) {
  const byId = new Map()

  const upsert = (id, label) => {
    const key = String(id || '').trim()
    if (!key) {
      return
    }
    const nextLabel = String(label || '').trim()
    if (!byId.has(key) || (nextLabel && !byId.get(key))) {
      byId.set(key, nextLabel || 'Deal owner')
    }
  }

  ;(Array.isArray(assignees) ? assignees : []).forEach(row => {
    upsert(row?.accountId, row?.name || row?.email)
  })

  if (accountId) {
    upsert(accountId, accountLabel)
  }
  if (currentOwnerAccountId) {
    upsert(currentOwnerAccountId, currentOwnerLabel)
  }

  const people = [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )

  return [{ value: '', label: 'Unassigned' }, ...people]
}

/**
 * Pull a plain string from Ant Design Select option shapes.
 * Ant Design 3 passes Option React elements (`option.props.children`);
 * newer shapes may expose `label` / `children` directly.
 * React nodes are ignored so they never become "[object Object]".
 *
 * @param {unknown} part
 * @returns {string}
 */
function ownerOptionText(part) {
  if (part == null) {
    return ''
  }
  if (typeof part === 'string' || typeof part === 'number') {
    return String(part)
  }
  return ''
}

/**
 * @param {{
 *   label?: unknown,
 *   children?: unknown,
 *   value?: unknown,
 *   props?: { label?: unknown, children?: unknown, value?: unknown },
 * }} [option]
 * @returns {{ label: string, value: string }}
 */
export function getCatchBoardOwnerOptionFields(option = {}) {
  const props = option?.props || {}
  return {
    label:
      ownerOptionText(option?.label) ||
      ownerOptionText(option?.children) ||
      ownerOptionText(props.label) ||
      ownerOptionText(props.children),
    value:
      ownerOptionText(option?.value) || ownerOptionText(props.value),
  }
}

/**
 * Ant Design Select filter for assignee name (antd 3 Option elements + label maps).
 * @param {string} input
 * @param {{
 *   label?: unknown,
 *   children?: unknown,
 *   value?: unknown,
 *   props?: { label?: unknown, children?: unknown, value?: unknown },
 * }} option
 * @returns {boolean}
 */
export function filterCatchBoardOwnerOption(input, option) {
  const query = String(input || '')
    .trim()
    .toLowerCase()
  if (!query) {
    return true
  }
  const { label, value } = getCatchBoardOwnerOptionFields(option)
  const haystack = `${label} ${value}`.toLowerCase()
  return haystack.includes(query)
}
